import {
  Dispatch,
  useEffect,
  useState,
  useMemo,
  useCallback,
  memo,
} from 'react'
import { EPlusFolder, FileStructure, TestFileCheck } from '../../types'
import { Disclosure, Transition } from '@headlessui/react'
import { MdOutlineKeyboardArrowDown } from 'react-icons/md'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'

type TreeProps = {
  fileStructure: FileStructure
  files: TestFileCheck[]
  folders: EPlusFolder[]
  setFiles: Dispatch<React.SetStateAction<TestFileCheck[]>>
  selectedFolders: string[]
  setSelectedFolders: (folders: string[]) => void
  searchMode: 0 | 1 | 2
}

type FileTreeNodeProps = {
  file: TestFileCheck | undefined
  checkFiles: (ids: string[], checked: boolean) => void
  setSelectedFolders: (folders: string[]) => void
  selectedFolders: string[]
}

interface FolderTreeNodeProps
  extends Omit<TreeProps, 'setFiles' | 'fileStructure'> {
  checkFiles: (ids: string[], checked: boolean) => void
  folder: EPlusFolder | undefined
  fileStructure: FileStructure | null
}

type CheckboxProps = {
  element: JSX.Element
  header: string
  open: boolean
  setOpen: Dispatch<React.SetStateAction<boolean>>
  setFolderCheck: () => void
  folderCheck: boolean
  htmlFor: string
}

const DisclosureCheckbox = memo(
  ({
    element,
    header,
    open,
    setOpen,
    folderCheck,
    setFolderCheck,
    htmlFor,
  }: CheckboxProps) => {
    const handleToggleOpen = useCallback(() => {
      setOpen(!open)
    }, [setOpen, open])

    return (
      <Disclosure defaultOpen={open}>
        <>
          <div className="flex  w-fit px-2">
            <div
              className="flex items-center gap-2"
              onChange={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                id={htmlFor}
                name={htmlFor}
                className="mr-2 accent-[#107C41] border-[#616161] w-4 h-4"
                checked={folderCheck}
                onChange={setFolderCheck}
              />
            </div>
            <Disclosure.Button
              onClick={handleToggleOpen}
              className="flex w-full flex-grow justify-between gap-1 rounded-b-none py-1 text-left text-sm font-medium text-black "
            >
              <MdOutlineKeyboardArrowDown
                className={`${open ? 'rotate-180 transform' : ''} h-5 w-5`}
              />
              <FolderOutlinedIcon sx={{ fontSize: 20 }} />
              <label htmlFor={htmlFor}>{header}</label>
            </Disclosure.Button>
          </div>
          {open && (
            <Transition
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
              as={'div'}
            >
              <Disclosure.Panel className="mt-2 px-4 pb-2 text-sm text-gray-500 bg-white rounded-b-lg ">
                {element}
              </Disclosure.Panel>
            </Transition>
          )}
        </>
      </Disclosure>
    )
  }
)

const filterFileAndFolder = (
  fileStructure: FileStructure | null,
  files: TestFileCheck[],
  folders: EPlusFolder[]
) => {
  // console.log('fileStructure:', fileStructure)
  if (!fileStructure) {
    return {
      files: [],
      folders: [],
    }
  }
  const fileIdSet = new Set<string>()
  const folderIdSet = new Set<string>()

  const helper = (subStructure: FileStructure | null) => {
    if (!subStructure) return

    const keys = Object.keys(subStructure)

    for (const k of keys) {
      if (subStructure[k] === null) {
        fileIdSet.add(k)
      } else {
        folderIdSet.add(k)
        helper(subStructure[k])
      }
    }
  }

  helper(fileStructure)
  return {
    files: files.filter((f) => fileIdSet.has(f.id)),
    folders: folders.filter((folder) => folderIdSet.has(folder.fileId)),
  }
}

export const FileTreeNode = memo(({ file, checkFiles }: FileTreeNodeProps) => {
  const handleCheckChange = useCallback(() => {
    if (file) {
      checkFiles([file.id], !file.checked)
    }
  }, [checkFiles, file])

  if (!file) return <></>

  return (
    <div className="flex justify-between items-center">
      <div className="flex items-center p-2">
        <input
          type="checkbox"
          name="select-all"
          className="mr-2 accent-[#107C41] border-[#616161] w-4 h-4"
          checked={file.checked}
          onChange={handleCheckChange}
          id={file.id}
        />
        <label className="cursor-pointer" htmlFor={file.id}>
          {file.filename}
        </label>
      </div>
    </div>
  )
})

export const FolderTreeNode = memo(
  ({
    fileStructure,
    files,
    folders,
    checkFiles,
    folder,
    selectedFolders,
    setSelectedFolders,
    searchMode,
  }: FolderTreeNodeProps) => {
    const [open, setOpen] = useState(false)
    const [folderCheck, setFolderCheck] = useState(false)

    // Memoize expensive computations
    const keys = useMemo(
      () => Object.keys(fileStructure ?? {}),
      [fileStructure]
    )

    const nestedFiles = useMemo(() => {
      if (!fileStructure) return []
      return keys
        .map((k) => {
          if (fileStructure[k] == null) {
            const file = files.find((f) => f.id === k)
            if (!file) return null
            return file
          }
          return null
        })
        .filter(Boolean) as TestFileCheck[]
    }, [fileStructure, keys, files])

    const nestedFolders = useMemo(() => {
      if (!fileStructure) return []
      return keys
        .map((key) => {
          if (fileStructure[key] == null) {
            return null
          }
          const { files: fs, folders: fos } = filterFileAndFolder(
            fileStructure[key],
            files,
            folders
          )
          const folder = folders.find((f) => f.fileId === key)
          if (!folder) return null
          return {
            folder,
            nestedFiles: fs,
            nestedFolders: fos,
          }
        })
        .filter(Boolean) as {
        folder: EPlusFolder
        nestedFiles: TestFileCheck[]
        nestedFolders: EPlusFolder[]
      }[]
    }, [fileStructure, keys, files, folders])

    const allowOpen = useMemo(
      () => (searchMode === 2 && nestedFolders.length === 0 ? false : true),
      [searchMode, nestedFolders.length]
    )

    const checkFolder = useCallback(() => {
      if (folderCheck) {
        checkFiles(
          files.map((file) => file.id),
          false
        )
        setFolderCheck(false)
        if (folder && selectedFolders.includes(folder.fileId)) {
          setSelectedFolders(selectedFolders.filter((f) => f !== folder.fileId))
        }
      } else {
        checkFiles(
          files.map((file) => file.id),
          true
        )
        setFolderCheck(true)
        if (folder && !selectedFolders.includes(folder.fileId)) {
          setSelectedFolders([...selectedFolders, folder.fileId])
        }
      }
    }, [
      folderCheck,
      checkFiles,
      files,
      folder,
      selectedFolders,
      setSelectedFolders,
    ])

    useEffect(() => {
      if (files.every((f) => f.checked) && !folderCheck) {
        setFolderCheck(true)
      } else if (files.some((f) => !f.checked) && folderCheck) {
        setFolderCheck(false)
      }
    }, [files, folderCheck])

    return files.length && folder && fileStructure ? (
      <DisclosureCheckbox
        header={folder.folderName}
        folderCheck={folderCheck}
        setFolderCheck={checkFolder}
        open={allowOpen ? open : false}
        setOpen={allowOpen ? setOpen : () => {}}
        htmlFor={folder.fileId}
        element={
          <div className="flex flex-col gap-2">
            {searchMode === 1 &&
              nestedFiles.length > 0 &&
              nestedFiles.map((file) => (
                <FileTreeNode
                  checkFiles={checkFiles}
                  file={file}
                  key={file.id}
                  selectedFolders={selectedFolders}
                  setSelectedFolders={setSelectedFolders}
                />
              ))}
            {nestedFolders.length > 0 &&
              nestedFolders.map(({ folder, nestedFiles, nestedFolders }) => (
                <FolderTreeNode
                  fileStructure={fileStructure[folder.fileId]}
                  files={nestedFiles}
                  folders={nestedFolders}
                  checkFiles={checkFiles}
                  key={folder.fileId}
                  folder={folder}
                  selectedFolders={selectedFolders}
                  setSelectedFolders={setSelectedFolders}
                  searchMode={searchMode}
                />
              ))}
          </div>
        }
      />
    ) : (
      <></>
    )
  }
)

const FilesTree = memo(
  ({
    fileStructure,
    files,
    folders,
    setFiles,
    selectedFolders,
    setSelectedFolders,
    searchMode,
  }: TreeProps) => {
    const keys = useMemo(() => Object.keys(fileStructure), [fileStructure])

    const checkFiles = useCallback(
      (ids: string[], checked: boolean) => {
        const idSet = new Set(ids)
        setFiles((files) =>
          files.map((f) => {
            if (idSet.has(f.id)) {
              return { ...f, checked }
            }
            return f
          })
        )
      },
      [setFiles]
    )

    const renderedNodes = useMemo(() => {
      return keys.map((k) => {
        if (fileStructure[k] == null) {
          const file = files.find((f) => f.id === k)
          if (searchMode === 1 && file)
            return (
              <FileTreeNode
                checkFiles={checkFiles}
                file={file}
                key={k}
                selectedFolders={selectedFolders}
                setSelectedFolders={setSelectedFolders}
              />
            )
          return <div className="hidden" key={k}></div>
        }
        const { files: fs, folders: fos } = filterFileAndFolder(
          fileStructure[k],
          files,
          folders
        )
        const folder = folders.find((f) => f.fileId === k)

        return (
          <FolderTreeNode
            key={k}
            fileStructure={fileStructure[k]}
            files={fs}
            folders={fos}
            checkFiles={checkFiles}
            folder={folder}
            selectedFolders={selectedFolders}
            setSelectedFolders={setSelectedFolders}
            searchMode={searchMode}
          />
        )
      })
    }, [
      keys,
      fileStructure,
      files,
      folders,
      searchMode,
      checkFiles,
      selectedFolders,
      setSelectedFolders,
    ])

    return <div className="flex flex-col gap-2">{renderedNodes}</div>
  }
)

export default FilesTree
